import { Link, useNavigate, useParams } from 'react-router-dom'
import { Button, InputCheckbox, LoadingSpinner, InlineAlert } from '../components/BaseComponents'
import { FormEvent, useEffect, useState } from 'react';
import usePublicApi from '../hooks/usePublicApi';
import { FORM_INPUT_TEXT_CLASS, FORM_LABEL_CLASS } from '../lib/formStyles';
import { INPUT_FOCUS_RING, TEXT_LINK_FOCUS_RING } from '../lib/a11yFocus';
import { getApiErrorMessage, getCSRFToken, validateEmail } from '../lib/utils';
import { getNewPasswordError, PASSWORD_HINT } from '../lib/passwordPolicy';
import routes from '../components/Router/routes';

/**
 * Signup for invited users (admin invites + supply-chain invites) - reads the token from the
 * `/invite/:token` path (Sub-task I Phase 1), unlike the open-signup `SignUp.tsx` page. Posts to
 * the same unchanged `POST /_api/user/signup` endpoint as `SignUp.tsx`.
 */
function InviteSignUp() {
    const { token: inviteToken } = useParams();
    const [termsConditions, setTermsConditions] = useState('');

    const [loading, setLoading] = useState(true);
    const [inviteNotFound, setInviteNotFound] = useState(false);

    const publicApi = usePublicApi();
    const navigate = useNavigate();

    useEffect(() => {
        let cancelled = false;
        const run = async () => {
            try {
                const [inviteResult, termsResult] = await Promise.all([
                    publicApi.get(`/invitations/${inviteToken}/preview`),
                    publicApi.get('user/loginTermsConditions'),
                ])
                if(cancelled) {
                    return;
                }
                if(!inviteResult.data?.isOpen) {
                    setInviteNotFound(true);
                    return;
                }
                setTermsConditions(termsResult.data.termsConditions)
            } catch (err) {
                if(cancelled) {
                    return;
                }
                console.error('Error loading invite info:', err);
                setInviteNotFound(true);
            } finally {
                if(!cancelled) {
                    setLoading(false);
                }
            }
        }
        run();
        return () => { cancelled = true; }
		// eslint-disable-next-line react-hooks/exhaustive-deps
    }, [inviteToken]);

    const [submitting, setSubmitting] = useState(false);
    const [error, setError] = useState('');

    const [email, setEmail] = useState('');
    const [name, setName] = useState('');
    const [password, setPassword] = useState('');
    const [confirmPassword, setConfirmPassword] = useState('');
    const [termsAccepted, setTermsAccepted] = useState(false);

    const onFormSubmit = (e: FormEvent) => {
       e.preventDefault();
       onSubmit();
       return false; 
    }

    const onSubmit = async () => {
        if(!name) {
            setError('Please enter your full name');
            return;
        } else if(!validateEmail(email)) {
            setError('Please enter a valid email address')
            return;
        } else if(getNewPasswordError(password, confirmPassword)) {
            setError(getNewPasswordError(password, confirmPassword) ?? '')
            return;
        } else if(!termsAccepted) {
            setError('You must confirm you are authorised to accept the Terms of Use to sign up.');
            return;
        }

        setSubmitting(true)
        setError('');

        try {
            await publicApi.post('/user/signup', {
                name,
                email,
                password,
                inviteToken,
                termsConditions,
                _csrf: await getCSRFToken(),
            });
			navigate(
                routes.publicLogin.path,
                {
                    replace: true,
                    state: {
                        message: 'Your account was successfully created, please login below'
                    }
                }
            )

        } catch(error: any) {
            setSubmitting(false);
            console.warn('Signup error:', error);
            if(error?.body?.code === 'ACCOUNT_EXISTS') {
                navigate(`${routes.acceptInvite.path}?inviteToken=${inviteToken}`, { replace: true });
                return;
            }
            setError(getApiErrorMessage(error, 'Failed to create your account. Please try again.'))
        }
    }

    if(loading) {
        return <LoadingSpinner />
    }

    if(inviteNotFound) {
        return (
            <div className="content w-full max-w-[32rem] px-4 sm:px-0 mx-auto mt-16">
                <div className="text-charcoal-100 text-2xl text-center">Invitation not found</div>
                <div className="text-charcoal-100 text-center mt-4">
                    This invitation link is invalid or has expired.
                    <br />
                    Please contact whoever invited you for a new link.
                </div>
                <div className="mt-6 text-center">
                    <Link className={`underline text-algae-500 ${TEXT_LINK_FOCUS_RING}`} to={routes.publicLogin.path}>Log in</Link>
                </div>
            </div>
        )
    }

    return (
        <div className="content w-full max-w-[32rem] px-4 sm:px-0 mx-auto mt-16 mb-8">
            <div className="text-charcoal-100 text-2xl text-center">Welcome</div>
            <div className="text-charcoal-100 text-2xl text-center">Setting up account</div>
            <form onSubmit={onFormSubmit}>
                <label className="block mt-8 text-sm">
                    <span className={FORM_LABEL_CLASS}>Your name</span>
                    <br />
                    <input
                        id="nameField"
                        disabled={submitting}
                        name="name"
                        className={`rounded w-full mt-1 ${FORM_INPUT_TEXT_CLASS} ${INPUT_FOCUS_RING}`}
                        type="text"
                        placeholder="e.g. Jo Bloggs"
                        value={name}
                        onChange={e => setName(e.target.value)}
                    />
                </label>
                <label className="block mt-8 text-sm">
                    <span className={FORM_LABEL_CLASS}>Verify your email address</span>
                    <br />
                    <input
                        id="emailField"
                        disabled={submitting}
                        name="email"
                        className={`rounded w-full mt-1 ${FORM_INPUT_TEXT_CLASS} ${INPUT_FOCUS_RING}`}
                        type="text"
                        placeholder="Enter the email address this invitation was sent to"
                        value={email}
                        onChange={e => setEmail(e.target.value)}
                    />
                </label>
                <label className="block mt-8 text-sm">
                    <span className={FORM_LABEL_CLASS}>Password</span>
                    <br />
                    <input
                        id="passwordField"
                        disabled={submitting}
                        name="password"
                        className={`rounded w-full mt-1 ${FORM_INPUT_TEXT_CLASS} ${INPUT_FOCUS_RING}`}
                        type="password"
                        autoComplete="new-password"
                        placeholder="Create a password"
                        value={password}
                        onChange={e => setPassword(e.target.value)}
                    />
                    <span className='block mt-1 text-xs text-charcoal-100'>{PASSWORD_HINT}</span>
                </label>
                <label className="block mt-8 text-sm">
                    <span className={FORM_LABEL_CLASS}>Confirm password</span>
                    <br />
                    <input
                        id="confirmPasswordField"
                        disabled={submitting}
                        name="confirmPassword"
                        className={`rounded w-full mt-1 ${FORM_INPUT_TEXT_CLASS} ${INPUT_FOCUS_RING}`}
                        type="password"
                        autoComplete="new-password"
                        placeholder="Confirm your password"
                        value={confirmPassword}
                        onChange={e => setConfirmPassword(e.target.value)}
                    />
                </label>

                <div
                    className="mt-8 max-h-[50vh] sm:max-h-96 overflow-y-scroll rounded font-sm"
                    role="region"
                    aria-label="Neoni® Beta 2 Terms of Use"
                    tabIndex={0}
                >
                    <div className="whitespace-pre-line text-charcoal-100">{ termsConditions }</div>
                </div>
                <label className="text-white mt-8 flex items-center gap-2">
                    <InputCheckbox
                        className='h-4 w-4 border-white rounded-sm text-algae-500 cursor-pointer active:ring-2 active:ring-transparent'
                        checked={termsAccepted}
                        onChange={e => setTermsAccepted(e.target.checked)}
                    />
                    <span>I am authorised to accept the Neoni® Beta 2 Terms of Use on behalf of my organisation.</span>
                </label>

                { error && (
                    <InlineAlert type='error' className='mt-6'>{ error }</InlineAlert>
                )}
                <div className='mt-6 flex justify-start'>
                    <Button
                        disabled={submitting}
                        buttonType="submit"
                        type="Primary"
                        text="Create an account"
                        context="form-field"
                    />
                </div>
            </form>
        </div>
    );
}

export default InviteSignUp
